import type {RichTextField} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import {AnimatePresence, motion} from 'framer-motion'
import type {IntegrationsContainerSliceV2} from 'prismicio-types'
import type {ChangeEvent} from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useDebounce} from 'react-use'
import {OPACITY_0} from 'src/common/constants'
import {Container} from 'src/common/Container'
import {EVENTS, track} from 'src/common/helpers'
import {getExtraCategories} from './getExtraCategories'
import {SearchBox} from './SearchBox'
import {ToolCardV2} from './ToolCardV2'
import useGetIntegrations from './userGetIntegrations'

/**
 * Props for `IntegrationsContainer`.
 */
export type IntegrationsContainerProps = {
  slice: IntegrationsContainerSliceV2 & {
    slice_type: string
    variation: string
  }
  index: number
} & {
  context: {pageUid: string}
}

/**
 * Component for "IntegrationsContainer" Slices.
 */
export const IntegrationsContainerV2 = ({
  slice,
  context,
  index: componentIndex,
}: IntegrationsContainerProps): JSX.Element => {
  const [searchText, setSearchText] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(0)
  const fixedCategories = useGetIntegrations(slice.primary)
  const extraCategories = useMemo(() => getExtraCategories(slice.primary), [slice.primary])
  const integrationsArr = useMemo(() => [...fixedCategories, ...extraCategories], [fixedCategories, extraCategories])

  const filterButtons = useMemo(
    () => [...slice.primary.categories, ...extraCategories.map(({name}) => ({category_name: name}))],
    [slice.primary.categories, extraCategories],
  )

  const [filteredIntegrations, setFilteredIntegrations] = useState(integrationsArr)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const searchBoxRef = useRef<HTMLDivElement | null>(null)
  const categoryTimeout = useRef<NodeJS.Timeout | null>(null)

  const changeCategory = (categoryIndex: number) => {
    containerRef.current?.scrollIntoView({behavior: 'smooth'})

    categoryTimeout.current = setTimeout(() => setSelectedCategory(categoryIndex), 600)
  }

  const filterBySearch = () => {
    if (searchText === '') {
      setFilteredIntegrations(integrationsArr)
      setLoading(false)
      return
    }

    const filtered = integrationsArr
      .map((category) => {
        const filteredTools = category.integrations.filter((integration) => {
          const text = (integration.name ?? '') + (integration.description ?? '')
          return text.toLowerCase().includes(searchText.toLowerCase())
        })

        return {
          ...category,
          integrations: filteredTools,
        }
      })
      .filter((category) => category.integrations.length > 0)

    setFilteredIntegrations(filtered)
  }

  const handleChange = ({currentTarget}: ChangeEvent<HTMLInputElement>) => {
    containerRef.current?.scrollIntoView({behavior: 'smooth'})
    setSearchText(currentTarget.value)
  }

  // Handle category selection
  useEffect(() => {
    const formattedIndex = selectedCategory - 1
    setLoading(true)

    if (formattedIndex >= 0) {
      setFilteredIntegrations([integrationsArr[formattedIndex]])
    } else if (formattedIndex === -1) {
      setFilteredIntegrations(integrationsArr)
    }

    setLoading(false)

    return () => clearTimeout(categoryTimeout.current!)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect when `selectedCategory` changes
  }, [selectedCategory, setFilteredIntegrations])

  const [,] = useDebounce(
    () => {
      setLoading(true)

      const searchTimeout = setTimeout(() => {
        filterBySearch()
      }, 700)

      setLoading(false)

      track({
        event: EVENTS.TEXT_INPUT,
        mktgId: 'search-bar',
        dataType: searchText,
        pageName: context.pageUid,
        location: `component-${componentIndex}`,
      })

      return () => clearTimeout(searchTimeout)
    },
    500,
    [searchText],
  )

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      backgroundColor="Grey/100"
      className="!overflow-visible"
      containerRef={containerRef}
    >
      <div className="grid-base overflow-visible px-5 md:px-10 lg:px-5 xl:px-0">
        <div className="col-span-4 md:col-span-full md:h-full md:py-6">
          <SearchBox
            handleChange={handleChange}
            searchText={searchText}
            categories={filterButtons}
            selectedCategory={selectedCategory}
            changeCategory={changeCategory}
            searchPlaceholder={slice.primary.search_placeholder}
            ref={searchBoxRef}
          />
        </div>
        <div className="col-span-full flex flex-col gap-12 md:col-span-full md:grid md:grid-cols-subgrid">
          <AnimatePresence>
            {!loading &&
              filteredIntegrations.map((category, categoryIndex) => (
                <motion.div
                  initial={OPACITY_0}
                  animate={{opacity: 1, transition: {duration: 0.3, delay: 0.3}}}
                  exit={{...OPACITY_0, transition: {duration: 0.3}}}
                  className="flex flex-col gap-6 md:col-span-full md:grid md:h-min md:grid-cols-subgrid"
                  key={`integration-category-${categoryIndex}`}
                >
                  <div className="col-span-full">
                    <PrismicRichText
                      field={category.title as RichTextField}
                      components={{
                        paragraph: ({children}) => <p className="text-h3 text-moss-grey-400">{children}</p>,
                        strong: ({children}) => <span className="text-black">{children}</span>,
                      }}
                    />
                  </div>
                  {category.integrations.map((integration, integrationIndex) => (
                    <ToolCardV2
                      integration={integration}
                      key={integration.name! + integrationIndex}
                      pageUid={context.pageUid}
                      index={integrationIndex}
                    />
                  ))}
                </motion.div>
              ))}
          </AnimatePresence>
        </div>
      </div>
    </Container>
  )
}
